import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import CodeEditor from '@uiw/react-textarea-code-editor';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useExperienceDefinition from '@/src/ExperienceBuilder/hooks/useExperienceDefinition';
import useExperienceVersions from '@/src/ExperienceBuilder/hooks/useExperienceVersions';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import {
    SchemaValidator,
    ValidationError,
} from '@/src/ExperienceBuilder/validation/schemaValidator';
import usePsv2Dark from '../components/usePsv2Dark';

/*
 * Capture v2 — experience management without rebuilding the builder:
 * status, three inline sources (upload JSON / apply template / import
 * from another activation), version history with restore, and a raw
 * JSON editor. Every write auto-versions server-side, so replacing an
 * experience is always undoable. The server does no validation —
 * uploads and edits gate through the client-side SchemaValidator.
 */

type SourceKey = 'upload' | 'template' | 'activation';

const MAX_ERRORS_SHOWN = 5;

function validationErrors(parsed: any): ValidationError[] {
    const result = SchemaValidator.getInstance().validateDefinition(parsed);
    return result.isValid
        ? []
        : result.errors.filter((e) => e.severity === 'error');
}

export default function CaptureV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const isDark = usePsv2Dark();
    const { getDefinition, addDefinition, saveDefinition, deleteDefinition } =
        useExperienceDefinition();
    const {
        versions,
        currentVersion,
        refresh: refreshVersions,
        restoreVersion,
        applyTemplate,
        importFromActivation,
    } = useExperienceVersions(id);
    const { getActivations } = useActivations();

    const [hasExperience, setHasExperience] = useState(false);
    const [stringDefinition, setStringDefinition] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [openSource, setOpenSource] = useState<SourceKey | null>(null);
    const [busy, setBusy] = useState(false);
    const [sourceError, setSourceError] = useState<string | null>(null);
    const [uploadIssues, setUploadIssues] = useState<ValidationError[]>([]);

    /* template state */
    const [templates, setTemplates] = useState<any[] | null>(null);
    const [templatesError, setTemplatesError] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [templateValues, setTemplateValues] = useState<
        Record<string, string>
    >({});

    /* import state */
    const [sourceActivations, setSourceActivations] = useState<any[] | null>(
        null,
    );
    const [importSearch, setImportSearch] = useState('');

    /* json editor state */
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorText, setEditorText] = useState('');
    const [editorIssues, setEditorIssues] = useState<ValidationError[]>([]);
    const [editorError, setEditorError] = useState<string | null>(null);

    const fileRef = useRef<HTMLInputElement>(null);
    const [restoring, setRestoring] = useState<number | null>(null);

    const load = async () => {
        const def = await getDefinition(id).catch(() => null);
        setHasExperience(!!def);
        setStringDefinition(
            def?.stringDefinition ??
                (def?.definition ? JSON.stringify(def.definition, null, 2) : ''),
        );
        setEditorText(
            def?.stringDefinition ??
                (def?.definition ? JSON.stringify(def.definition, null, 2) : ''),
        );
        await refreshVersions();
    };

    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        (async () => {
            try {
                await load();
            } finally {
                if (mounted) setIsLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, orgId]);

    const reload = async () => {
        setBusy(true);
        try {
            await load();
            setOpenSource(null);
            setSelectedTemplate(null);
            setEditorIssues([]);
            setEditorError(null);
        } finally {
            setBusy(false);
        }
    };

    const toggleSource = (key: SourceKey) => {
        setSourceError(null);
        setUploadIssues([]);
        setOpenSource((s) => (s === key ? null : key));
        if (key === 'template' && templates == null) {
            authenticatedApi
                .get(`/${orgId}/experience-templates`)
                .then((r) =>
                    setTemplates(
                        (Array.isArray(r.data) ? r.data : []).filter(
                            (t: any) => t.templateType === 1,
                        ),
                    ),
                )
                .catch((e) => {
                    setTemplates([]);
                    setTemplatesError(
                        e?.response?.status === 404
                            ? 'No templates available on your license.'
                            : 'Could not load templates.',
                    );
                });
        }
        if (key === 'activation' && sourceActivations == null) {
            getActivations()
                .then((rows: any[]) =>
                    setSourceActivations(
                        (rows ?? []).filter(
                            (a) => a.id !== id && !a.isArchived,
                        ),
                    ),
                )
                .catch(() => setSourceActivations([]));
        }
    };

    /* ---- upload ---- */
    const handleFile = async (file: File) => {
        setSourceError(null);
        setUploadIssues([]);
        const text = await file.text();
        let parsed: any;
        try {
            parsed = JSON.parse(text);
        } catch {
            setSourceError(`${file.name} isn't valid JSON.`);
            return;
        }
        const issues = validationErrors(parsed);
        if (issues.length > 0) {
            setUploadIssues(issues);
            return;
        }
        setBusy(true);
        try {
            const body = JSON.stringify(parsed);
            const ok = hasExperience
                ? await saveDefinition(id, body).catch(() => null)
                : await addDefinition(id, body).catch(() => null);
            if (ok != null) {
                await reload();
            } else {
                setSourceError('Save failed — try again.');
            }
        } finally {
            setBusy(false);
        }
    };

    /* ---- template ---- */
    const pickTemplate = (t: any) => {
        setSelectedTemplate(t);
        setSourceError(null);
        try {
            const parsed = JSON.parse(t.definitionJsonString);
            setTemplateValues(
                Object.fromEntries(
                    Object.entries(parsed?.templateValues ?? {}).map(
                        ([k, v]) => [k, String(v ?? '')],
                    ),
                ),
            );
        } catch {
            setTemplateValues({});
        }
    };

    const applySelectedTemplate = async () => {
        if (!selectedTemplate || busy) return;
        setBusy(true);
        setSourceError(null);
        const result = await applyTemplate(
            selectedTemplate.id,
            templateValues,
        );
        if (result != null) {
            await reload();
        } else {
            setSourceError('Applying the template failed.');
        }
        setBusy(false);
    };

    /* ---- import ---- */
    const importFrom = async (sourceId: number) => {
        if (busy) return;
        setBusy(true);
        setSourceError(null);
        const result = await importFromActivation(sourceId);
        if (result != null) {
            await reload();
        } else {
            setSourceError(
                'Import failed — that activation may not have an experience.',
            );
        }
        setBusy(false);
    };

    /* ---- json editor ---- */
    const editorDirty = editorText !== stringDefinition;

    const saveEditor = async () => {
        if (busy) return;
        setEditorError(null);
        setEditorIssues([]);
        let parsed: any;
        try {
            parsed = JSON.parse(editorText);
        } catch (e: any) {
            setEditorError(`Not valid JSON — ${e?.message ?? 'parse error'}`);
            return;
        }
        const issues = validationErrors(parsed);
        if (issues.length > 0) {
            setEditorIssues(issues);
            return;
        }
        setBusy(true);
        try {
            const ok = await saveDefinition(
                id,
                JSON.stringify(parsed),
            ).catch(() => null);
            if (ok != null) {
                await load();
            } else {
                setEditorError('Save failed — try again.');
            }
        } finally {
            setBusy(false);
        }
    };

    const restore = async (versionNumber: number) => {
        if (restoring != null) return;
        setRestoring(versionNumber);
        const result = await restoreVersion(versionNumber);
        if (result != null) await load();
        setRestoring(null);
    };

    const filteredSources = (sourceActivations ?? []).filter((a) =>
        `${a.name}`.toLowerCase().includes(importSearch.toLowerCase()),
    );

    const issueList = (issues: ValidationError[]) => (
        <div className="mt-2 rounded-[10px] bg-secondary px-3.5 py-2.5">
            <div className="text-[11.5px] font-semibold text-attention">
                {issues.length} validation error
                {issues.length === 1 ? '' : 's'} — nothing was saved
            </div>
            {issues.slice(0, MAX_ERRORS_SHOWN).map((e, i) => (
                <div key={i} className="mt-1 text-[11.5px]">
                    <span className="font-mono text-[10.5px] text-muted-foreground">
                        {e.path}
                    </span>{' '}
                    <span className="text-muted-foreground">{e.message}</span>
                </div>
            ))}
            {issues.length > MAX_ERRORS_SHOWN && (
                <div className="mt-1 text-[11px] text-faint">
                    +{issues.length - MAX_ERRORS_SHOWN} more
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[720px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    const sources: { key: SourceKey; title: string; desc: string }[] = [
        {
            key: 'upload',
            title: 'Upload JSON',
            desc: 'A definition file exported from the builder or another tool.',
        },
        {
            key: 'template',
            title: 'From a template',
            desc: 'Start from a template on your license, with its custom values.',
        },
        {
            key: 'activation',
            title: 'From another activation',
            desc: "Copy an existing event's experience.",
        },
    ];

    return (
        <main className="mx-auto max-w-[720px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Capture experience</div>

            {/* status */}
            <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2.5">
                        <span
                            className={
                                'h-2 w-2 rounded-full ' +
                                (hasExperience ? 'bg-good' : 'bg-off')
                            }
                        />
                        <h1 className="text-[17px] font-bold tracking-[-0.01em]">
                            {hasExperience
                                ? 'Experience configured'
                                : 'No experience yet'}
                        </h1>
                    </div>
                    <p className="mt-1.5 max-w-[52ch] text-[13px] leading-relaxed text-muted-foreground">
                        {hasExperience ? (
                            <>
                                {currentVersion != null &&
                                    `Version ${currentVersion} is live. `}
                                Devices running clickerbooth load this
                                experience.
                            </>
                        ) : (
                            <>
                                ROMR shoots don't need one — the app captures
                                on its own. Activations using clickerbooth
                                require an experience before devices can
                                capture.
                            </>
                        )}
                    </p>
                </div>
                <span className="flex flex-none items-center gap-3">
                    {hasExperience && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    const blob = new Blob(
                                        [stringDefinition],
                                        { type: 'application/json' },
                                    );
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `experience-${id}.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                }}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                Export JSON
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (
                                        !window.confirm(
                                            'Delete this experience? Versions remain restorable server-side, but devices will have nothing to load.',
                                        )
                                    )
                                        return;
                                    await deleteDefinition(id).catch(
                                        () => null,
                                    );
                                    setIsLoading(true);
                                    await load();
                                    setIsLoading(false);
                                }}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint hover:text-attention"
                            >
                                Delete
                            </button>
                        </>
                    )}
                </span>
            </div>

            {/* sources */}
            <section className="mb-9">
                <div className="mb-1 flex items-baseline justify-between">
                    <span className="ps-label">
                        {hasExperience ? 'Replace experience' : 'Start from'}
                    </span>
                    {hasExperience && (
                        <span className="text-[11.5px] text-faint">
                            Every change is versioned — restore below
                        </span>
                    )}
                </div>
                {sources.map((s, i) => (
                    <div
                        key={s.key}
                        className={i > 0 ? 'border-t border-line' : ''}
                    >
                        <button
                            type="button"
                            onClick={() => toggleSource(s.key)}
                            className="flex w-full cursor-pointer items-baseline gap-2.5 border-0 bg-transparent px-0.5 py-3 text-left font-[inherit]"
                        >
                            <span className="text-[13.5px] font-semibold">
                                {s.title}
                            </span>
                            <span className="min-w-0 truncate text-[12px] text-faint">
                                {s.desc}
                            </span>
                            <span className="ml-auto text-[11px] text-faint">
                                {openSource === s.key ? '▴' : '▾'}
                            </span>
                        </button>

                        {openSource === s.key && (
                            <div className="pb-4 pl-0.5">
                                {s.key === 'upload' && (
                                    <>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() =>
                                                fileRef.current?.click()
                                            }
                                            className="cursor-pointer rounded-full border border-border bg-transparent px-4 py-1.5 font-[inherit] text-[12.5px] font-semibold text-foreground hover:border-faint"
                                        >
                                            {busy
                                                ? 'Saving…'
                                                : 'Choose a .json file'}
                                        </button>
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="application/json,.json"
                                            className="hidden"
                                            onChange={(e) => {
                                                const f =
                                                    e.target.files?.[0];
                                                if (f) handleFile(f);
                                                e.target.value = '';
                                            }}
                                        />
                                        {uploadIssues.length > 0 &&
                                            issueList(uploadIssues)}
                                    </>
                                )}

                                {s.key === 'template' && (
                                    <>
                                        {templates == null && (
                                            <p className="text-[12.5px] text-faint">
                                                Loading templates…
                                            </p>
                                        )}
                                        {templatesError && (
                                            <p className="text-[12.5px] text-muted-foreground">
                                                {templatesError}
                                            </p>
                                        )}
                                        {templates != null &&
                                            !templatesError &&
                                            templates.length === 0 && (
                                                <p className="text-[12.5px] text-muted-foreground">
                                                    No experience templates on
                                                    your license.
                                                </p>
                                            )}
                                        {(templates ?? []).map((t) => (
                                            <div key={t.id}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        pickTemplate(t)
                                                    }
                                                    className={
                                                        'block w-full cursor-pointer border-0 bg-transparent px-0.5 py-2 text-left font-[inherit] text-[12.5px] ' +
                                                        (selectedTemplate?.id ===
                                                        t.id
                                                            ? 'font-semibold text-brand-ink'
                                                            : 'font-semibold hover:text-brand-ink')
                                                    }
                                                >
                                                    {t.name}
                                                    {t.description && (
                                                        <span className="ml-2 font-normal text-faint">
                                                            {t.description}
                                                        </span>
                                                    )}
                                                </button>
                                                {selectedTemplate?.id ===
                                                    t.id && (
                                                    <div className="mb-2 pl-0.5">
                                                        {Object.keys(
                                                            templateValues,
                                                        ).length > 0 && (
                                                            <div className="grid gap-x-6 gap-y-3 py-2 sm:grid-cols-2">
                                                                {Object.entries(
                                                                    templateValues,
                                                                ).map(
                                                                    ([
                                                                        k,
                                                                        v,
                                                                    ]) => (
                                                                        <div
                                                                            key={
                                                                                k
                                                                            }
                                                                        >
                                                                            <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                                                                                {
                                                                                    k
                                                                                }
                                                                            </label>
                                                                            <div className="flex items-center gap-2">
                                                                                {/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(
                                                                                    v,
                                                                                ) && (
                                                                                    <input
                                                                                        type="color"
                                                                                        value={
                                                                                            v
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            setTemplateValues(
                                                                                                (
                                                                                                    tv,
                                                                                                ) => ({
                                                                                                    ...tv,
                                                                                                    [k]: e
                                                                                                        .target
                                                                                                        .value,
                                                                                                }),
                                                                                            )
                                                                                        }
                                                                                        className="h-7 w-9 cursor-pointer rounded-[6px] border border-border bg-transparent p-0.5"
                                                                                    />
                                                                                )}
                                                                                <input
                                                                                    className="ps-input w-full font-mono text-[12px]"
                                                                                    value={
                                                                                        v
                                                                                    }
                                                                                    onChange={(
                                                                                        e,
                                                                                    ) =>
                                                                                        setTemplateValues(
                                                                                            (
                                                                                                tv,
                                                                                            ) => ({
                                                                                                ...tv,
                                                                                                [k]: e
                                                                                                    .target
                                                                                                    .value,
                                                                                            }),
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            disabled={busy}
                                                            onClick={
                                                                applySelectedTemplate
                                                            }
                                                            className="mt-1 cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                                                        >
                                                            {busy
                                                                ? 'Applying…'
                                                                : 'Apply template'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </>
                                )}

                                {s.key === 'activation' && (
                                    <>
                                        <SearchField
                                            className="mb-1.5 w-full"
                                            placeholder="Search activations…"
                                            value={importSearch}
                                            onChange={setImportSearch}
                                        />
                                        {sourceActivations == null && (
                                            <p className="text-[12.5px] text-faint">
                                                Loading activations…
                                            </p>
                                        )}
                                        <div className="max-h-[220px] overflow-y-auto">
                                            {filteredSources
                                                .slice(0, 30)
                                                .map((a) => (
                                                    <button
                                                        key={a.id}
                                                        type="button"
                                                        disabled={busy}
                                                        onClick={() =>
                                                            importFrom(a.id)
                                                        }
                                                        className="flex w-full cursor-pointer items-baseline gap-2.5 border-0 bg-transparent px-0.5 py-1.5 text-left font-[inherit] text-[12.5px] hover:text-brand-ink"
                                                    >
                                                        <span className="font-semibold">
                                                            {a.name}
                                                        </span>
                                                        <span className="ml-auto whitespace-nowrap text-[11px] text-faint">
                                                            {moment(
                                                                a.date,
                                                            ).format(
                                                                'MMM D, YYYY',
                                                            )}
                                                        </span>
                                                    </button>
                                                ))}
                                            {sourceActivations != null &&
                                                filteredSources.length ===
                                                    0 && (
                                                    <p className="py-1 text-[12.5px] text-muted-foreground">
                                                        No activations match.
                                                    </p>
                                                )}
                                        </div>
                                        <p className="mt-1.5 text-[11px] text-faint">
                                            Importing copies the source
                                            experience — pick one that has one.
                                        </p>
                                    </>
                                )}

                                {sourceError && (
                                    <p className="mt-2 text-[12px] font-semibold text-attention">
                                        {sourceError}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </section>

            {/* version history */}
            {versions.length > 0 && (
                <section className="mb-9">
                    <div className="ps-label mb-1.5">Version history</div>
                    {versions.slice(0, 8).map((v, i) => (
                        <div
                            key={v.id}
                            className={
                                'flex items-baseline gap-2.5 py-2.5 text-[12.5px] ' +
                                (i > 0 ? 'border-t border-line' : '')
                            }
                        >
                            <span className="font-semibold tabular-nums">
                                v{v.versionNumber}
                            </span>
                            {v.versionNumber === currentVersion ? (
                                <span className="rounded-full bg-good-soft px-2 py-0.5 text-[10.5px] font-semibold text-good">
                                    live
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    disabled={restoring != null}
                                    onClick={() => restore(v.versionNumber)}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                                >
                                    {restoring === v.versionNumber
                                        ? 'Restoring…'
                                        : 'Restore'}
                                </button>
                            )}
                            <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-faint">
                                {v.source}
                            </span>
                            {v.createdBy && (
                                <span className="text-muted-foreground">
                                    {v.createdBy}
                                </span>
                            )}
                            <span className="ml-auto whitespace-nowrap text-[11.5px] text-faint">
                                {moment(v.createdAt).fromNow()}
                            </span>
                        </div>
                    ))}
                </section>
            )}

            {/* json editor */}
            {hasExperience && (
                <section className="mb-9">
                    <div className="mb-1.5 flex h-7 items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setEditorOpen((o) => !o)}
                            className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                        >
                            Definition {editorOpen ? '▴' : '▾'}
                        </button>
                        {editorOpen && editorDirty && (
                            <button
                                type="button"
                                disabled={busy}
                                onClick={saveEditor}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                            >
                                {busy ? 'Saving…' : 'Save changes'}
                            </button>
                        )}
                    </div>
                    {editorOpen && (
                        <>
                            <div className="overflow-hidden rounded-[12px] border border-border">
                                <CodeEditor
                                    value={editorText}
                                    language="json"
                                    onChange={(e) =>
                                        setEditorText(e.target.value)
                                    }
                                    padding={14}
                                    data-color-mode={isDark ? 'dark' : 'light'}
                                    style={{
                                        fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        fontSize: 12,
                                        background: 'transparent',
                                        minHeight: 220,
                                        maxHeight: 480,
                                        overflow: 'auto',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                    }}
                                />
                            </div>
                            {editorError && (
                                <p className="mt-2 text-[12px] font-semibold text-attention">
                                    {editorError}
                                </p>
                            )}
                            {editorIssues.length > 0 &&
                                issueList(editorIssues)}
                        </>
                    )}
                </section>
            )}
        </main>
    );
}
